/**
 * DistrictDefaultCellsEditor — ADR 116 / ADR 117
 *
 * Cross-protocol district-level rubric editor. Edits cells with shape
 * `(district, role, urgency=set, mode=set, alert_type=null)` — the
 * DEFAULT cell variant. These rows propagate to every
 * AlertType in the district that matches the `(urgency, mode)` combo
 * unless a school-tier override (T2) or a type-override at either
 * tier supersedes.
 *
 * This is a different table shape than `ProtocolRecipientsEditor` —
 * that editor is `roles × {Real Alert, Drill}` for ONE protocol. This
 * editor is `roles × (urgency, mode)` across ALL protocols. The two
 * grids share the underlying append-only rubric API and
 * the materialization signals — only the cell-key shape
 * differs.
 *
 * Mount point: `Sites > Protocols` page when the page-level scope
 * shift is set to "District template". Permission-
 * gated on `schools.change_districtalertrubriccell`.
 *
 * Why the layout differs from per-protocol Recipients
 * ===================================================
 * A roles × `(urgency × mode)` grid spreads N roles down and (2 × 2 =
 * 4) urgency-mode combos across. Putting Real Alert + Drill
 * checkboxes + a level picker in each cell would be unreadably busy.
 * Compromise: the cell shows two compact dropdowns (Real Alert, Drill),
 * each carrying Unsubscribed / L0 / L1 / L2. Levels are independent per
 * axis — each dropdown writes its own `*_min_level` column on the cell.
 */
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Spinner,
  Text,
  Center,
  Select,
  Portal,
  createListCollection,
} from '@chakra-ui/react';
import { useGroups } from '../../hooks/queries';
import {
  useDistrictRubricCells,
  useCreateDistrictRubricCell,
  usePatchDistrictRubricCell,
  useDeleteDistrictRubricCell,
} from '../../hooks/queries/useRubricCells';
import type { RubricCell, RubricMode, RubricUrgency } from '../../services';
import { useScopedFeature } from '../../hooks/useFeature';
import { FeatureCode } from '../../types/features';

interface DistrictDefaultCellsEditorProps {
  districtId: number;
}

const LEVELS = [0, 1, 2] as const;
type Level = (typeof LEVELS)[number];

// `follow` is excluded — it's an incident-only urgency at the
// AlertType level, not an "alert" in customer-facing terms. See
// `RubricUrgency` doc in `services/rubricCells.service.ts` for the
// full reasoning. The grid is roles × (2 × 2) = 4 cells per row.
const URGENCIES: RubricUrgency[] = ['emergency', 'attention'];
const MODES: RubricMode[] = ['alert', 'report'];

// One dropdown per subscription rather than a checkbox + level picker.
// `Unsubscribed` writes null on the wire, L0/L1/L2 write 0/1/2. Reads
// inverse via `levelToDropdownValue`.
//
// Why: "subscribed?" and "at what level?" are functionally one value
// from the user's perspective ("the user shouldn't have to think hard"),
// so one widget with an explicit `Unsubscribed` option.
const UNSUBSCRIBED = 'unsubscribed';

/**
 * The option list is a factory, not a module-level const.
 * ADR-117 Mechanism B is a per-org feature flag, so a single shared
 * collection would leak the "1st/2nd Escalation" options into every grid
 * cell regardless of the flag. The component memoises one collection per
 * flag state and hands the same instance to every cell.
 */
export function buildSubscriptionCollection(
  escalationEnabled: boolean,
  t: (key: string, fallback: string) => string,
) {
  return createListCollection({
    items: [
      {
        label: t('protocols.unsubscribed', 'Unsubscribed'),
        value: UNSUBSCRIBED,
      },
      { label: t('protocols.alertStart', 'Alert Start'), value: '0' },
      ...(escalationEnabled
        ? [
            {
              label: t('protocols.firstEscalation', '1st Escalation'),
              value: '1',
            },
            {
              label: t('protocols.secondEscalation', '2nd Escalation'),
              value: '2',
            },
          ]
        : []),
    ],
  });
}

function dropdownToLevel(value: string): number | null {
  if (value === UNSUBSCRIBED) return null;
  const n = Number(value);
  return Number.isFinite(n) && LEVELS.includes(n as Level) ? n : null;
}

export function levelToDropdownValue(
  level: number | null | undefined,
  escalationEnabled: boolean,
): string {
  if (level == null) return UNSUBSCRIBED;
  if (!LEVELS.includes(level as Level)) return UNSUBSCRIBED;
  // Clamp a stored level 1/2 to the "Alert Start" option so the
  // closed dropdown reads correctly instead of falling back to the
  // "Unsubscribed" placeholder. Display-only — the cell is never rewritten.
  return String(escalationEnabled || level <= 0 ? level : 0);
}

function findDefaultCell(
  cells: RubricCell[],
  roleId: number,
  urgency: RubricUrgency,
  mode: RubricMode,
): RubricCell | undefined {
  return cells.find(
    (c) =>
      c.role === roleId &&
      c.urgency === urgency &&
      c.mode === mode &&
      c.alert_type === null,
  );
}

export function DistrictDefaultCellsEditor({
  districtId,
}: DistrictDefaultCellsEditorProps) {
  const { t } = useTranslation();
  const urgencyLabel = useCallback(
    (urgency: RubricUrgency) => {
      switch (urgency) {
        case 'emergency':
          return t('protocols.emergency', 'Emergency');
        case 'attention':
          return t('protocols.attention', 'Attention');
      }
    },
    [t],
  );

  const modeLabel = useCallback(
    (mode: RubricMode) => {
      switch (mode) {
        case 'alert':
          return t('events.toggle.alerts', 'Alert');
        case 'report':
          return t('protocols.incident', 'Incident');
      }
    },
    [t],
  );

  // ADR-117 Mechanism B gate, resolved for the district this grid
  // writes to. NOT `useFeature` — that unions across every visible site when
  // no active organization is selected, so an Aegix-internal admin managing a
  // flag-ON district alongside this one would be offered escalation options
  // here that the backend then 400s. Fails closed: an unloaded payload or a
  // district absent from it reads OFF.
  const escalationEnabled = useScopedFeature(
    FeatureCode.ALERT_ESCALATION,
    'organization',
    districtId,
  );
  const subscriptionCollection = useMemo(
    () => buildSubscriptionCollection(escalationEnabled, t),
    [escalationEnabled, t],
  );
  const { data: groupsData, isLoading: isLoadingGroups } = useGroups({
    page_size: 200,
    // Hide Internal Staff and Vendor Staff roles from recipient pickers,
    // even for callers with `accounts.view_all_roles` (ADR-100). They're
    // not school-side notification targets.
    exclude_hidden: true,
  });
  const { data: cells = [], isLoading: isLoadingCells } =
    useDistrictRubricCells(districtId, {});

  const createMut = useCreateDistrictRubricCell(districtId);
  const patchMut = usePatchDistrictRubricCell(districtId);
  const deleteMut = useDeleteDistrictRubricCell(districtId);

  // `Unsubscribed` is a first-class dropdown value, so each dropdown
  // encodes its own value directly — no pending-level state is needed.
  // No per-cell `RubricCellPreviewBadge` on this surface; the
  // preview-impact endpoint and the badge component stay available for
  // other surfaces.
  //
  // Pure alphabetical sort by display name — no `is_system_role`-first
  // precedence, since this picker shows no "System role" distinction.
  // Locale-aware comparison so accented characters slot in sensibly.
  const visibleRoles = useMemo(() => {
    const all = groupsData?.results ?? [];
    return [...all].sort((a, b) =>
      (a.display_name || a.name).localeCompare(
        b.display_name || b.name,
        undefined,
        { sensitivity: 'base' },
      ),
    );
  }, [groupsData]);

  const writeCell = useCallback(
    async (
      roleId: number,
      urgency: RubricUrgency,
      mode: RubricMode,
      next: { alert_min_level: number | null; drill_min_level: number | null },
    ) => {
      const existing = findDefaultCell(cells, roleId, urgency, mode);
      const isClear =
        next.alert_min_level === null && next.drill_min_level === null;
      if (isClear) {
        if (!existing) return;
        await deleteMut.mutateAsync(existing.id);
        return;
      }
      if (existing) {
        await patchMut.mutateAsync({
          cellId: existing.id,
          body: {
            alert_min_level: next.alert_min_level,
            drill_min_level: next.drill_min_level,
          },
        });
        return;
      }
      await createMut.mutateAsync({
        role: roleId,
        urgency,
        mode,
        alert_type: null,
        alert_min_level: next.alert_min_level,
        drill_min_level: next.drill_min_level,
      });
    },
    [cells, createMut, patchMut, deleteMut, districtId],
  );

  if (isLoadingGroups || isLoadingCells) {
    return (
      <Center py={6}>
        <Spinner size="sm" />
      </Center>
    );
  }

  if (visibleRoles.length === 0) {
    return (
      <Text fontSize="sm" color="text.secondary">
        {t(
          'protocols.noRolesAvailable',
          'No roles available — ask an admin to define at least one role before configuring district defaults.',
        )}
      </Text>
    );
  }

  const isMutating =
    createMut.isPending || patchMut.isPending || deleteMut.isPending;

  return (
    <Box
      opacity={isMutating ? 0.7 : 1}
      pointerEvents={isMutating ? 'none' : 'auto'}
    >
      <Text fontSize="sm" color="text.secondary" mb={3}>
        {t(
          'protocols.districtDefaultsDescription',
          'These defaults apply to every protocol in the district matching the urgency × mode combination, unless a school override (T2) or a per-protocol override at either tier supersedes. Each cell sets the Alert (real alert) and Drill subscription levels independently.',
        )}
      </Text>

      {/* Header row */}
      <Flex
        gap={2}
        px={2}
        py={2}
        borderBottom="1px solid"
        borderColor="border.secondary"
        alignItems="center"
      >
        <Box flex="0 0 180px">
          <Text fontSize="xs" color="text.secondary" fontWeight="medium">
            {t('common.role', 'Role')}
          </Text>
        </Box>
        {URGENCIES.map((urgency) =>
          MODES.map((mode) => (
            <Box
              key={`${urgency}-${mode}`}
              flex="1"
              minW="140px"
              textAlign="center"
            >
              <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                {urgencyLabel(urgency)} · {modeLabel(mode)}
              </Text>
            </Box>
          )),
        )}
      </Flex>

      {visibleRoles.map((role) => (
        <Flex
          key={role.id}
          gap={2}
          px={2}
          py={2}
          alignItems="center"
          borderBottom="1px solid"
          borderColor="border.subtle"
          data-e2e={`district-default-row-${role.id}`}
        >
          <Box flex="0 0 180px">
            <Text fontSize="sm" color="text.primary">
              {role.display_name || role.name}
            </Text>
            {/* No "System role" subtitle — role catalogue distinction
                lives in role management, not on this picker. Sort order
                is pure alphabetical (see `visibleRoles` above). */}
          </Box>
          {URGENCIES.map((urgency) =>
            MODES.map((mode) => {
              const cell = findDefaultCell(cells, role.id, urgency, mode);
              const alertValue = levelToDropdownValue(
                cell?.alert_min_level,
                escalationEnabled,
              );
              const drillValue = levelToDropdownValue(
                cell?.drill_min_level,
                escalationEnabled,
              );

              const onAlertChange = (newValue: string) => {
                void writeCell(role.id, urgency, mode, {
                  alert_min_level: dropdownToLevel(newValue),
                  drill_min_level: cell?.drill_min_level ?? null,
                });
              };
              const onDrillChange = (newValue: string) => {
                void writeCell(role.id, urgency, mode, {
                  alert_min_level: cell?.alert_min_level ?? null,
                  drill_min_level: dropdownToLevel(newValue),
                });
              };

              // Renders one dropdown for either the Alert or Drill
              // subscription with values [Unsubscribed, L0, L1, L2].
              const renderSubscriptionSelect = (
                kind: 'alert' | 'drill',
                label: string,
                value: string,
                onChange: (v: string) => void,
              ) => {
                // Name matches the visible row/column context —
                // display transforms, not wire values.
                const a11yName = `${label} subscription — ${role.display_name || role.name}, ${urgencyLabel(urgency)} ${modeLabel(mode)}`;
                return (
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    w="100%"
                  >
                    <Text
                      fontSize="xs"
                      color="text.secondary"
                      fontWeight="medium"
                      w="10"
                      flexShrink={0}
                    >
                      {label}
                    </Text>
                    <Box flex="1" minW="100px">
                      <Select.Root
                        collection={subscriptionCollection}
                        value={[value]}
                        onValueChange={(details) => {
                          const v = details.value[0];
                          if (v) onChange(v);
                        }}
                        size="xs"
                        data-e2e={`district-default-${kind}-${role.id}-${urgency}-${mode}`}
                      >
                        <Select.HiddenSelect aria-label={a11yName} />
                        <Select.Control>
                          <Select.Trigger aria-label={a11yName}>
                            <Select.ValueText placeholder="Unsubscribed" />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                          <Select.Positioner>
                            <Select.Content>
                              {subscriptionCollection.items.map((item) => (
                                <Select.Item item={item} key={item.value}>
                                  <Select.ItemText>
                                    {item.label}
                                  </Select.ItemText>
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Portal>
                      </Select.Root>
                    </Box>
                  </Flex>
                );
              };

              return (
                <Flex
                  key={`${role.id}-${urgency}-${mode}`}
                  flex="1"
                  minW="180px"
                  direction="column"
                  alignItems="stretch"
                  justifyContent="center"
                  gap={1}
                  px={2}
                >
                  {renderSubscriptionSelect(
                    'alert',
                    'Alert',
                    alertValue,
                    onAlertChange,
                  )}
                  {renderSubscriptionSelect(
                    'drill',
                    'Drill',
                    drillValue,
                    onDrillChange,
                  )}
                  {/* No per-cell `RubricCellPreviewBadge` (the "up to N
                      users · M sites" hover detail) here: the cell
                      affordance stays focused on the two dropdowns. The
                      preview-impact endpoint + badge component stay
                      available for other surfaces. */}
                </Flex>
              );
            }),
          )}
        </Flex>
      ))}
    </Box>
  );
}
